import { PermissionService } from './permission/permission.service';
import { Router } from '@angular/router';
import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { User } from '../models/user.model';
import { environment } from 'src/environments/environment';
import { CacheService } from './cache.service';
import { RouteStateService } from './route-state.service';
import { UserService } from './user-service.service';
import { Observable, from, lastValueFrom } from 'rxjs';
import { isNullOrEmpty } from '../utils/common-functions';
import { LocalStorageService } from './local-storage.service';
import { GlobalConstants } from '../constants/global-constants.constant';
import { UserPreference } from '../models/user-preference';
import { SessionStorageService } from './session-storage.service';
import { UserRefreshToken } from '../models/user-refreshToken';
import { UserType } from '../enums/user-type.enum';
import { clearDetectorDetectInterval } from 'src/app/shared/devtools/utils/interval';
import { disableKeyAndMenu } from 'src/app/shared/devtools/utils/key-menu';
import { TranslateService } from '@ngx-translate/core';
import { MsalService } from '@azure/msal-angular';
import { CommonDataService } from './common-data.service';
import { isLaasPortalEmbeddedInParentFrame } from '../utils/embedding.util';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private static readonly IAM_MIGRATED_USER_TYPES_CSM_CODE = 'IAM_MIGRATED_USER_TYPES';
  private static readonly IAM_MIGRATED_USER_TYPES_CACHE_TTL_MS = 5 * 60 * 1000;

  public onLogout: EventEmitter<void>;
  public onSystemUpgradeRequired: EventEmitter<void>;
  private iamMigratedUserTypesCache?: { values: Set<string>; expiresAt: number };

  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly cacheService: CacheService,
    private readonly localStorageService: LocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly routeStateService: RouteStateService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly msalService: MsalService,
    private readonly commonDataService: CommonDataService,
    private readonly permissionService: PermissionService) {
    this.onLogout = new EventEmitter<void>();
    this.onSystemUpgradeRequired = new EventEmitter<void>();
  }

  get isAuthenticated(): boolean {
    return !isNullOrEmpty(this.currentToken);
  }

  get currentUserInfo(): User {
    return this.userService.userInfo;
  }

  get currentToken(): string {
    return this.localStorageService.getItem(GlobalConstants.currentToken);
  }

  set currentToken(token: string) {
    this.localStorageService.setItem(GlobalConstants.currentToken, token);
  }

  get language(): string {
    return this.localStorageService.getItem(GlobalConstants.languageKey);
  }
  get isEmbedded(): boolean {
    return isLaasPortalEmbeddedInParentFrame();
  }

  login(username: string, password: string, loginType: string, mfaIgnoreStatus?: boolean): Observable<any> {
    const promise = new Promise<void>(async (resolve, reject) => {
      try {
        const jwtResp = await lastValueFrom(this.getToken(username, password, loginType, mfaIgnoreStatus, undefined, this.language, undefined));
        if (jwtResp) {
          if (!isNullOrEmpty(jwtResp.redirectUrl)) {
            this.router.navigateByUrl(jwtResp.redirectUrl);
            resolve(jwtResp);
            return;
          }

          if (jwtResp.passwordExpired) {
            resolve(jwtResp);
            return;
          }

          if (mfaIgnoreStatus || (loginType === 'portal' && jwtResp.token)) {
            this.intoLaasFrontPage(jwtResp);
          }

          resolve(jwtResp);
        } else {
          const msg = this.translateService.instant('Login.Message.AuthenticatonFailed');
          reject(new Error(msg));
        }
      } catch (e) {
        reject(e);
      }
    });
    return from(promise);
  }

  logout(): void {
    this.onLogout.emit();
    this.iamMigratedUserTypesCache = undefined;
    this.clear();
    this.userService.logout();
    this.cacheService.clear();
    this.routeStateService.clear();
    this.sessionStorageService.clear();
    this.permissionService.clearCache();
    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts || accounts.length > 0) {
      // this.msalService.logoutRedirect(); //會跳出azure登出視窗
      this.router.navigate(['/login']);
    }
  }

  clear(): void {
    this.localStorageService.removeItem(GlobalConstants.currentToken);
  }

  async refreshTokenBy(userRefreshToken: UserRefreshToken) {
    // 嵌入新系統時，token 由外殼透過 /portal/session/bootstrap 派發；
    // portal 自身不該再去打舊 /authenticationByLoginAccount，否則被遷移過的
    // userType 會撞 SYSTEM_UPGRADED 403。
    if (this.isEmbedded) {
      return;
    }
    this.getToken(
      this.userService.userInfo.loginAccount,
      'none',
      'external',
      true,
      userRefreshToken.facility,
      userRefreshToken.language,
      this.userService.userInfo.userType
    ).subscribe(resp => {
      this.currentToken = resp.token;
    });
  }

  private getToken(userName: string, password: string, loginType: string, mfaIgnoreStatus?: boolean, facility?: string, language?: string, userType?: string): Observable<any> {
    return loginType === 'external' ?
      this.http.post<any>(`${environment.authUrl}/authenticationByLoginAccount`, {
        userName: userName,
        password: null,
        facility: facility,
        language: language,
        userType: userType }) :
      this.http.post<any>(`${environment.authUrl}/authentication`, {
        userName: userName,
        password,
        facility: facility,
        language: language,
        userType: userType,
        mfaIgnoreStatus: mfaIgnoreStatus });
  }

  preLogin(username: string, password: string, language: string): Observable<any> {
    const promise = new Promise<any>(async (resolve, reject) => {
      try {
        console.log("Pre login");
        const resp = await lastValueFrom(this.getTermsFlagAndActivationStatus(username, password, language));
        resolve(resp);
      }
      catch (e) {
        reject(e);
      }
    });
    return from(promise);
  }

  private getTermsFlagAndActivationStatus(userName: string, password: string, language: string): Observable<any> {
    return this.http.post<any>(`${environment.authUrl}/user/getTermsFlagAndActivationStatus`, {
      userName: userName,
      password,
      language: language
    });
  }

  sendVerifivationCode(userName: string, password: string): Observable<any> {
    return this.http.post<any>(`${environment.authUrl}/sendVerifivationCode`,
      {
        userName: userName,
        password,
        language: this.language
      });
  }

  async clearVerficationCode(userName: string): Promise<void> {
    await lastValueFrom(this.http.post<any>(`${environment.authUrl}/clearVerficationCode`, { userName: userName }));
  }

  verifyVerificationCode(userName: string, password: string, language: string, verificationCode: string): Observable<any> {
    const promise = new Promise<void>(async (resolve, reject) => {
      try {
        const jwtResp = await this.http.post<any>(`${environment.authUrl}/verifyVerificationCode`,
        {
          userName,
          password,
          language,
          verificationCode
        }).toPromise();

        if (!jwtResp) {
          const msg = this.translateService.instant('Login.Message.AuthenticatonFailed');
          reject(new Error(msg));
        }

        if (jwtResp.passwordExpired) {
          this.currentToken = jwtResp.token;
          resolve(jwtResp);
          return;
        }

        this.intoLaasFrontPage(jwtResp);
        resolve(jwtResp);
      } catch (e) {
        reject(e);
      }
    });
    return from(promise);
  }

  async intoLaasFrontPage(jwtResp?: any) {
    if (jwtResp) {
      this.currentToken = jwtResp.token;
    }
    const userProfile = await lastValueFrom(this.userService.getUserProfile());
    console.log("userProfile = " + userProfile.userProfile);
    console.log("userProfile.userType = " + userProfile.userProfile.userType);
    const user: User = {
      id: userProfile.userProfile.uuid,
      loginAccount: userProfile.userProfile.loginAccount,
      userName: userProfile.userProfile.userName,
      email: userProfile.userProfile.email,
      userType: userProfile.userProfile.userType
    };

    console.log("user.userType = " + user.userType);
    this.userService.userInfo = user;

    const userPreference = await lastValueFrom(this.userService.getUserPreferences());

    console.log(userPreference);

    if (user.userType === UserType.SuperAdmin) {
      clearDetectorDetectInterval();
      disableKeyAndMenu(false);
    }

    this.userService.preference = isNullOrEmpty(userPreference) ? new UserPreference() : userPreference;

    try {
      await lastValueFrom(this.permissionService.loadUserWhitelists());
    } catch (e) {
      console.error('白名單載入失敗', e);
    }

    // 種子用戶攔截：Shipping 類型用戶強制導向新系統
    if (!this.isEmbedded && await this.shouldShowSystemUpgradeDialog(user.userType)) {
      this.onSystemUpgradeRequired.emit();
      return;
    }

    this.router.navigateByUrl(`/`);
  }

  async validateJwtToken(): Promise<any> {
    try {
      const authorization = `Bearer ${this.currentToken}`;
      return await lastValueFrom(this.http.get<boolean>(`${environment.authUrl}/validateJwtToken`, { headers: { Authorization: authorization } }));
    } catch (error) {
      console.error("Token validation failed", error);
      return false;
    }
  }

  public async shouldShowSystemUpgradeDialog(userType: string): Promise<boolean> {
    if (isNullOrEmpty(userType)) {
      return false;
    }
    const migratedUserTypes = await this.getIamMigratedUserTypes();
    return migratedUserTypes.has(userType.toLowerCase());
  }

  private async getIamMigratedUserTypes(): Promise<Set<string>> {
    const now = Date.now();
    if (this.iamMigratedUserTypesCache && now < this.iamMigratedUserTypesCache.expiresAt) {
      return this.iamMigratedUserTypesCache.values;
    }

    try {
      const setting = await this.commonDataService
        .getAppCsmSetting(AuthenticationService.IAM_MIGRATED_USER_TYPES_CSM_CODE)
        .toPromise();

      const values = new Set<string>();
      if (setting?.isActivated === 1 && setting.appCsmSettingDetailList?.length) {
        setting.appCsmSettingDetailList
          .filter(detail => detail.isActivated === 1 && !isNullOrEmpty(detail.setupValue))
          .forEach(detail => values.add(detail.setupValue.trim().toLowerCase()));
      }

      this.iamMigratedUserTypesCache = {
        values,
        expiresAt: now + AuthenticationService.IAM_MIGRATED_USER_TYPES_CACHE_TTL_MS,
      };
      return values;
    } catch (error) {
      console.warn('Failed to load IAM_MIGRATED_USER_TYPES from AppCsmSetting', error);
      const fallback = new Set<string>();
      this.iamMigratedUserTypesCache = {
        values: fallback,
        expiresAt: now + AuthenticationService.IAM_MIGRATED_USER_TYPES_CACHE_TTL_MS,
      };
      return fallback;
    }
  }
}

export interface OnLogout {
  onLogout(): void;
}
